import React, { useState } from "react";
import {
  Plus,
  Copy,
  Check,
  Trash2,
  RotateCcw,
  MoreHorizontal,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { format } from "date-fns";
import SectionHeader from "@/components/v2/SectionHeader";
import EmptyState from "@/components/v2/EmptyState";
import TableSkeleton from "@/components/v2/TableSkeleton";
import ConfirmDialog from "@/components/v2/ConfirmDialog";
import WebhookSourceSheet from "@/components/v2/WebhookSourceSheet";
import useWebhookSources from "@/components/v2/hooks/useWebhookSources";

export default function WebhookSourcesTab() {
  const { sources, loading, error, refetch, createSource, updateSource, deleteSource, rotateToken } = useWebhookSources();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [rotateConfirm, setRotateConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const handleCreate = async (data) => createSource(data);
  const handleUpdate = async (data) => {
    if (!editingSource) return false;
    return await updateSource(editingSource.id, data);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    await deleteSource(deleteConfirm.id);
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const handleRotate = async () => {
    if (!rotateConfirm) return;
    setRotating(true);
    const newToken = await rotateToken(rotateConfirm.id);
    if (newToken) {
      try {
        await navigator.clipboard.writeText(newToken);
        toast.success("New token copied to clipboard");
      } catch (e) {
        toast.success("Token rotated — copy it now from the source");
      }
    }
    setRotating(false);
    setRotateConfirm(null);
  };

  const copyToken = async (source) => {
    const token = source.token || source.token_preview || "";
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      setCopiedId(source.id);
      toast.success("Token copied");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (e) {
      toast.error("Copy failed");
    }
  };

  const maskToken = (token) => {
    if (!token) return "—";
    const last4 = token.slice(-4);
    return `••••${last4}`;
  };

  if (error) {
    return (
      <div className="bg-surface-2 border border-border rounded-sm p-6">
        <div className="flex flex-col items-center gap-3 py-8">
          <AlertCircle className="w-8 h-8 text-loss" />
          <p className="text-sm text-muted-foreground">Failed to load webhook sources</p>
          <Button size="sm" variant="outline" onClick={refetch} className="rounded-sm border-border">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-2 border border-border rounded-sm p-4">
      <SectionHeader
        title="Webhook Sources"
        description="Manage incoming webhook integrations"
        actionLabel="Add Source"
        actionIcon={Plus}
        onAction={() => { setEditingSource(null); setSheetOpen(true); }}
      />

      {loading ? (
        <TableSkeleton rows={5} columns={5} />
      ) : sources.length === 0 ? (
        <EmptyState text="No webhook sources yet. Create one to start receiving webhook events." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="border-border">
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Name</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Token</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Strategies</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold">Created</TableHead>
              <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sources.map((source) => (
              <TableRow key={source.id} className="border-border">
                <TableCell className="font-medium text-sm">{source.name}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-xs text-muted-foreground">
                      {maskToken(source.token || source.token_preview)}
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToken(source)}
                      className="h-6 w-6 p-0 rounded-sm"
                      title="Copy token"
                    >
                      {copiedId === source.id ? (
                        <Check className="w-3 h-3 text-profit" />
                      ) : (
                        <Copy className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="text-[10px] border-border">
                    {source.strategy_count ?? source.strategies_count ?? 0}
                  </Badge>
                </TableCell>
                <TableCell>
                  <span className="font-mono text-xs text-muted-foreground">
                    {source.created_at ? format(new Date(source.created_at), "dd MMM yyyy") : "—"}
                  </span>
                </TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm">
                        <MoreHorizontal className="w-3.5 h-3.5" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-surface-2 border-border rounded-sm min-w-[160px]">
                      <DropdownMenuItem
                        className="text-xs"
                        onClick={() => { setEditingSource(source); setSheetOpen(true); }}
                      >
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-xs"
                        onClick={() => setRotateConfirm(source)}
                      >
                        <RotateCcw className="w-3 h-3 mr-1.5" />
                        Rotate Token
                      </DropdownMenuItem>
                      <DropdownMenuSeparator className="bg-border" />
                      <DropdownMenuItem
                        className="text-xs text-loss"
                        onClick={() => setDeleteConfirm(source)}
                      >
                        <Trash2 className="w-3 h-3 mr-1.5" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <WebhookSourceSheet
        open={sheetOpen}
        onOpenChange={(o) => { setSheetOpen(o); if (!o) setEditingSource(null); }}
        onSubmit={editingSource ? handleUpdate : handleCreate}
        editingSource={editingSource}
      />

      <ConfirmDialog
        open={!!deleteConfirm}
        onOpenChange={(o) => { if (!o) setDeleteConfirm(null); }}
        onConfirm={handleDelete}
        title="Delete Webhook Source"
        description={`Are you sure you want to delete "${deleteConfirm?.name}"? This will break any integrations using this source.`}
        confirmLabel="Delete"
        variant="destructive"
        loading={deleting}
      />

      <ConfirmDialog
        open={!!rotateConfirm}
        onOpenChange={(o) => { if (!o) setRotateConfirm(null); }}
        onConfirm={handleRotate}
        title="Rotate Token?"
        description="Existing integrations using this token will stop working until updated with the new token."
        confirmLabel="Rotate"
        variant="destructive"
        loading={rotating}
      />
    </div>
  );
}
